"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSearchLocationsByInput } from "@/components/modules/search/api/useSearchLocationsByInput";
import { useLocationsById } from "@/hooks/QueryHooks/useLocationsById";
import { useBoundaries } from "@/hooks/useBoundaries";
import { administrativeLevelForCode } from "@/lib/boundaries/boundaryCache";
import { convertPolygon } from "@/components/pages/search/map/components/MapPolygons";
import { SearchTypes } from "@/constants/filters";
import { clampMapSelection, isMaxLocationsReached, toggleCodeWithCap } from "./locationCap";
export type SearchLocation = {
  code: string | null;
  name: string;
  proxyName?: string;
  administrativeLevel: string;
  proxyAdministrativeLevel?: string;
  realEstateCount?: number;
};
export function useFormMultiLocationInput(name, maxLocations) {
  const $ = _c(55);
  const {
    control,
    setValue
  } = useFormContext();
  const locationCodes = useWatch({
    control,
    name
  }) ?? [];
  const [searchInput, setSearchInput] = useState("");
  const [queryInput, setQueryInput] = useState("");
  const timerRef = useRef(undefined);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function handleSearchChange(value) {
      setSearchInput(value);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setQueryInput(value), 400);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleSearchChange = t0;
  let t1;
  if ($[1] !== queryInput) {
    t1 = {
      input: queryInput,
      businessType: SearchTypes.RENT_ROOM
    };
    $[1] = queryInput;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: searchResults,
    isLoading: isSearching
  } = useSearchLocationsByInput(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [nameCache, setNameCache] = useState(t2);
  const {
    data: locationNamesData
  } = useLocationsById({
    locations: locationCodes,
    enabled: locationCodes.length > 0
  });
  let t3;
  if ($[4] !== locationNamesData || $[5] !== setNameCache) {
    t3 = () => {
      if (!locationNamesData?.data) {
        return;
      }
      setNameCache(prev => {
        let changed = false;
        const next = new Map(prev);
        for (const loc of locationNamesData.data) {
          if (loc.code && !next.has(loc.code)) {
            next.set(loc.code, loc.name);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
    $[4] = locationNamesData;
    $[5] = setNameCache;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== locationNamesData) {
    t4 = [locationNamesData];
    $[7] = locationNamesData;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== setNameCache) {
    t5 = function cacheNameFromSearch(code, name_0) {
      setNameCache(prev_0 => {
        if (prev_0.has(code)) {
          return prev_0;
        }
        const next_0 = new Map(prev_0);
        next_0.set(code, name_0);
        return next_0;
      });
    };
    $[9] = setNameCache;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const cacheNameFromSearch = t5;
  let t6;
  if ($[11] !== nameCache) {
    t6 = code_0 => ({
      code: code_0,
      name: nameCache.get(code_0) ?? code_0
    });
    $[11] = nameCache;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const selectedLocations = locationCodes.map(t6);
  const geometryByCode = useBoundaries(locationCodes.map(_temp));
  let t7;
  if ($[13] !== geometryByCode) {
    t7 = code_2 => convertPolygon(geometryByCode.get(code_2)) as [number, number][];
    $[13] = geometryByCode;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const locationPolygons = locationCodes.map(t7).filter(_temp2);
  let t8;
  if ($[15] !== searchResults) {
    t8 = searchResults?.data ?? searchResults ?? [];
    $[15] = searchResults;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const locations = t8 as Array<SearchLocation>;
  let t9;
  if ($[17] !== cacheNameFromSearch || $[18] !== locationCodes || $[19] !== maxLocations || $[20] !== name || $[21] !== setValue) {
    t9 = function handleToggleCode(code_3, locationName) {
      if (locationName) {
        cacheNameFromSearch(code_3, locationName);
      }
      const newCodes = toggleCodeWithCap(locationCodes, code_3, maxLocations);
      if (newCodes === null) {
        return;
      }
      setValue(name, newCodes, {
        shouldValidate: true,
        shouldDirty: true
      });
    };
    $[17] = cacheNameFromSearch;
    $[18] = locationCodes;
    $[19] = maxLocations;
    $[20] = name;
    $[21] = setValue;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleToggleCode = t9;
  let t10;
  if ($[23] !== locationCodes || $[24] !== name || $[25] !== setValue) {
    t10 = function handleRemoveCode(code_4) {
      setValue(name, locationCodes.filter(c => c !== code_4), {
        shouldValidate: true,
        shouldDirty: true
      });
    };
    $[23] = locationCodes;
    $[24] = name;
    $[25] = setValue;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const handleRemoveCode = t10;
  let t11;
  if ($[27] !== name || $[28] !== setValue) {
    t11 = function handleClearAll() {
      setValue(name, [], {
        shouldValidate: true,
        shouldDirty: true
      });
    };
    $[27] = name;
    $[28] = setValue;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const handleClearAll = t11;
  let t12;
  if ($[30] !== locationCodes || $[31] !== maxLocations || $[32] !== name || $[33] !== setNameCache || $[34] !== setValue) {
    t12 = function handleMapSelectionChange(codes, nameByCode) {
      if (nameByCode) {
        setNameCache(prev_1 => {
          let changed_0 = false;
          const next_1 = new Map(prev_1);
          for (const [code_5, locName] of nameByCode) {
            if (!next_1.has(code_5)) {
              next_1.set(code_5, locName);
              changed_0 = true;
            }
          }
          return changed_0 ? next_1 : prev_1;
        });
      }
      const limitedCodes = clampMapSelection(locationCodes, codes, maxLocations);
      setValue(name, limitedCodes, {
        shouldValidate: true,
        shouldDirty: true
      });
    };
    $[30] = locationCodes;
    $[31] = maxLocations;
    $[32] = name;
    $[33] = setNameCache;
    $[34] = setValue;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const handleMapSelectionChange = t12;
  let t13;
  if ($[36] !== locationCodes.length || $[37] !== maxLocations) {
    t13 = isMaxLocationsReached(locationCodes.length, maxLocations);
    $[36] = locationCodes.length;
    $[37] = maxLocations;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const isMaxReached = t13;
  let t14;
  if ($[39] !== control || $[40] !== handleClearAll || $[41] !== handleMapSelectionChange || $[42] !== handleRemoveCode || $[43] !== handleSearchChange || $[44] !== handleToggleCode || $[45] !== isMaxReached || $[46] !== isSearching || $[47] !== locationCodes || $[48] !== locationPolygons || $[49] !== locations || $[50] !== queryInput || $[51] !== searchInput || $[52] !== selectedLocations || $[53] !== setValue) {
    t14 = {
      control,
      setValue,
      locationCodes,
      searchInput,
      queryInput,
      isSearching,
      locations,
      selectedLocations,
      locationPolygons,
      isMaxReached,
      handleSearchChange,
      handleToggleCode,
      handleRemoveCode,
      handleClearAll,
      handleMapSelectionChange
    };
    $[39] = control;
    $[40] = handleClearAll;
    $[41] = handleMapSelectionChange;
    $[42] = handleRemoveCode;
    $[43] = handleSearchChange;
    $[44] = handleToggleCode;
    $[45] = isMaxReached;
    $[46] = isSearching;
    $[47] = locationCodes;
    $[48] = locationPolygons;
    $[49] = locations;
    $[50] = queryInput;
    $[51] = searchInput;
    $[52] = selectedLocations;
    $[53] = setValue;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  return t14;
}
function _temp2(p) {
  return p.length > 0;
}
function _temp(code_1) {
  return {
    locationCode: code_1,
    administrative_level: administrativeLevelForCode(code_1)
  };
}